/**
 * SurgicalStrikeSkillEffect — Blades tree sunder path Fork 2-A active.
 *
 * Three rapid thrusts to different anatomical zones on a single target.
 * At 2 or more Keen Edge stacks, the third hit is a guaranteed critical.
 * Critical hits from Surgical Strike apply a 3-second bleed. Long cooldown.
 *
 * Phase machine: idle → active (3 timed sub-hits) → cooldown → idle.
 * Cooldown starts after the final sub-hit completes.
 *
 * Each hit resolves independently (separate crit rolls). Hit 3 checks
 * Keen Edge stacks for guaranteed crit. After a guaranteed crit,
 * Keen Edge resets to resolver-provided value (0 by default, 1 with
 * Systematic upgrade).
 *
 * Implements SkillEffect directly with own cooldown and phase management.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Surgical Strike
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SurgicalStrikeDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startAttack(attackType: string): number;
  };
  readonly keenEdge: {
    getStacks(targetId: string): number;
    resetToValue(targetId: string, value: number): void;
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
  };
  readonly target: {
    getCurrentTargetId(): string;
  };
  readonly crit: {
    rollCrit(bonusCritChance: number): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SurgicalStrikeConfig {
  readonly cooldownMs: number;
  readonly subHitCount: number;
  readonly subHitIntervalMs: number;
  readonly guaranteedCritThreshold: number;
  readonly bleedDurationMs: number;
  readonly bleedIntensity: number;
  readonly bleedStacks: number;
}

const DEFAULT_CONFIG: SurgicalStrikeConfig = {
  cooldownMs: 1500,                 // long cooldown tier
  subHitCount: 3,                   // three rapid thrusts
  subHitIntervalMs: 100,            // 100ms between each thrust
  guaranteedCritThreshold: 2,       // 2+ Keen Edge stacks for guaranteed crit
  bleedDurationMs: 3000,            // 3s bleed on crit
  bleedIntensity: 3,                // bleed tick intensity
  bleedStacks: 1,                   // 1 bleed stack per crit
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SurgicalStrikePhase = 'idle' | 'active' | 'cooldown';

/** Per-hit record for assertions and state inspection. */
export interface HitRecord {
  hitNumber: number;
  enemiesHit: number;
  wasCrit: boolean;
  wasGuaranteedCrit: boolean;
  bleedApplied: boolean;
  keenEdgeResetCalled: boolean;
  keenEdgeResetValue: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SurgicalStrikeSkillEffect implements SkillEffect {
  readonly skillId = 'surgical_strike';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SurgicalStrikeDeps;
  private readonly config: SurgicalStrikeConfig;

  private _phase: SurgicalStrikePhase = 'idle';
  private _phaseTimer = 0;
  private _subHitsRemaining = 0;
  private _subHitTimer = 0;
  private _activationCount = 0;
  private _hitLog: HitRecord[] = [];

  constructor(deps: SurgicalStrikeDeps, config: Partial<SurgicalStrikeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): SurgicalStrikePhase { return this._phase; }
  get activationCount(): number { return this._activationCount; }
  get hitLog(): readonly HitRecord[] { return this._hitLog; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle';
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Enter active phase
    this._phase = 'active';
    this._subHitsRemaining = this.config.subHitCount;
    this._subHitTimer = 0;
    this._hitLog = [];
    this._activationCount++;

    // Fire first sub-hit immediately
    this.fireSubHit();

    return true;
  }

  deactivate(): void {
    // Cannot manually deactivate during sequence
  }

  isActive(): boolean {
    return this._phase === 'active';
  }

  update(delta: number): void {
    switch (this._phase) {
      case 'active':
        if (this._subHitsRemaining > 0) {
          this._subHitTimer += delta;
          if (this._subHitTimer >= this.config.subHitIntervalMs) {
            this._subHitTimer -= this.config.subHitIntervalMs;
            this.fireSubHit();
          }
        } else {
          // All sub-hits fired — enter cooldown
          this._phase = 'cooldown';
          this._phaseTimer = this.config.cooldownMs;
        }
        break;

      case 'cooldown':
        this._phaseTimer = Math.max(0, this._phaseTimer - delta);
        if (this._phaseTimer <= 0) {
          this._phase = 'idle';
        }
        break;
    }
  }

  destroy(): void {
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._subHitsRemaining = 0;
  }

  // --- Internal ---

  private fireSubHit(): void {
    if (this._subHitsRemaining <= 0) return;

    const hitNumber = this.config.subHitCount - this._subHitsRemaining + 1;
    const isFinalHit = hitNumber === this.config.subHitCount;
    this._subHitsRemaining--;

    const enemiesHit = this.deps.combat.startAttack('surgical_strike');

    const record: HitRecord = {
      hitNumber,
      enemiesHit,
      wasCrit: false,
      wasGuaranteedCrit: false,
      bleedApplied: false,
      keenEdgeResetCalled: false,
      keenEdgeResetValue: 0,
    };

    if (enemiesHit > 0) {
      const targetId = this.deps.target.getCurrentTargetId();
      let wasCrit = false;
      let wasGuaranteedCrit = false;

      // Keen Edge crit bonus applies to ALL hits, not just the final one
      const stacks = this.deps.keenEdge.getStacks(targetId);
      const critPerStack = this.deps.resolver.getAdditiveEffect('surgical_strike', 'critPerStack');
      const keenEdgeCritBonus = stacks * critPerStack;

      if (isFinalHit && stacks >= this.config.guaranteedCritThreshold) {
        wasCrit = true;
        wasGuaranteedCrit = true;
      } else {
        wasCrit = this.deps.crit.rollCrit(keenEdgeCritBonus);
      }

      record.wasCrit = wasCrit;
      record.wasGuaranteedCrit = wasGuaranteedCrit;

      // Apply bleed on any crit
      if (wasCrit) {
        this.deps.statusEffects.apply('bleed', {
          sourceSkillId: 'surgical_strike',
          durationMs: this.config.bleedDurationMs,
          intensity: this.config.bleedIntensity,
          stacks: this.config.bleedStacks,
        });
        record.bleedApplied = true;
      }

      // Reset Keen Edge after guaranteed crit on final hit
      if (wasGuaranteedCrit) {
        const resetValue = Math.max(
          0,
          this.deps.resolver.getMaxEffect('surgical_strike', 'keenEdgeResetValue') ?? 0,
        );
        this.deps.keenEdge.resetToValue(targetId, resetValue);
        record.keenEdgeResetCalled = true;
        record.keenEdgeResetValue = resetValue;
      }
    }

    this._hitLog.push(record);
  }
}
